import { ValidationRule, ValidationType, ValidationResult, FormSubmissionData } from '../models/forms/form-models';

/**
 * Individual validator functions following Single Responsibility Principle
 */
export class FormValidators {

  static required(value: any): boolean {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (typeof value === 'boolean') return value;
    if (Array.isArray(value)) return value.length > 0;
    return Boolean(value);
  }

  static minLength(value: string, minLength: number): boolean {
    return value.length >= minLength;
  }

  static maxLength(value: string, maxLength: number): boolean {
    return value.length <= maxLength;
  }

  static emailFormat(value: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  }

  static phoneFormat(value: string): boolean {
    // Flexible phone format - accepts various formats
    const phoneRegex = /^[\\+]?[\d\s\-()]{10,}$/;
    return phoneRegex.test(value.replace(/\s/g, ''));
  }

  static minValue(value: number, minValue: number): boolean {
    return value >= minValue;
  }

  static maxValue(value: number, maxValue: number): boolean {
    return value <= maxValue;
  }

  static pattern(value: string, pattern: string): boolean {
    const regex = new RegExp(pattern);
    return regex.test(value);
  }
}

/**
 * Validation service following Open/Closed Principle
 * Can be extended with new validation types without modifying existing code
 */
export class ValidationService {

  /**
   * Validates a single field value against its validation rules
   */
  static validateField(value: any, rules: ValidationRule[]): string | null {
    for (const rule of rules) {
      let isValid = false;

      switch (rule.type) {
        case ValidationType.REQUIRED:
          isValid = FormValidators.required(value);
          break;

        case ValidationType.MIN_LENGTH:
          isValid = FormValidators.minLength(value, rule.value);
          break;

        case ValidationType.MAX_LENGTH:
          isValid = FormValidators.maxLength(value, rule.value);
          break;

        case ValidationType.EMAIL_FORMAT:
          isValid = FormValidators.emailFormat(value);
          break;

        case ValidationType.PHONE_FORMAT:
          isValid = FormValidators.phoneFormat(value);
          break;

        case ValidationType.MIN_VALUE:
          isValid = FormValidators.minValue(value, rule.value);
          break;

        case ValidationType.MAX_VALUE:
          isValid = FormValidators.maxValue(value, rule.value);
          break;

        case ValidationType.PATTERN:
          isValid = FormValidators.pattern(value, rule.value);
          break;

        case ValidationType.CUSTOM:
          if (rule.customValidator) {
            isValid = rule.customValidator(value);
          }
          break;

        default:
          // Unknown validation type - log warning in development
          if (process.env.NODE_ENV === 'development') {
          }
          isValid = true;
      }

      if (!isValid) {
        return rule.message;
      }
    }

    return null; // No validation errors
  }

  /**
   * Validates entire form data against form configuration
   */
  static validateForm(formData: FormSubmissionData, formFields: any[]): ValidationResult {
    const errors: Record<string, string> = {};
    let isValid = true;

    // Create a flat map of all fields with their validation rules
    const fieldValidationMap = new Map();

    formFields.forEach(section => {
      if (section.fields) {
        section.fields.forEach((field: any) => {
          if (field.validationRules) {
            fieldValidationMap.set(field.name, field.validationRules);
          }
        });
      }
    });

    // Validate each field
    for (const [fieldName, validationRules] of fieldValidationMap) {
      const fieldValue = formData[fieldName];
      const errorMessage = this.validateField(fieldValue, validationRules);

      if (errorMessage) {
        errors[fieldName] = errorMessage;
        isValid = false;
      }
    }

    return { isValid, errors };
  }

  /**
   * Sanitizes and prepares form data for submission
   */
  static sanitizeFormData(formData: FormSubmissionData): FormSubmissionData {
    const sanitized: FormSubmissionData = {};

    for (const [key, value] of Object.entries(formData)) {
      if (typeof value === 'string') {
        // Trim whitespace from string values
        sanitized[key] = value.trim();
      } else if (Array.isArray(value)) {
        // Filter out empty values from arrays
        sanitized[key] = value.filter(item =>
          item !== null && item !== undefined && item !== ''
        );
      } else {
        sanitized[key] = value;
      }
    }

    return sanitized;
  }
}

/**
 * Factory for creating common validation rules
 */
export class ValidationRuleFactory {

  static required(message = 'This field is required'): ValidationRule {
    return {
      type: ValidationType.REQUIRED,
      message
    };
  }

  static minLength(length: number, message?: string): ValidationRule {
    return {
      type: ValidationType.MIN_LENGTH,
      value: length,
      message: message || `Minimum ${length} characters required`
    };
  }

  static maxLength(length: number, message?: string): ValidationRule {
    return {
      type: ValidationType.MAX_LENGTH,
      value: length,
      message: message || `Maximum ${length} characters allowed`
    };
  }

  static email(message = 'Please enter a valid email address'): ValidationRule {
    return {
      type: ValidationType.EMAIL_FORMAT,
      message
    };
  }

  static phone(message = 'Please enter a valid phone number'): ValidationRule {
    return {
      type: ValidationType.PHONE_FORMAT,
      message
    };
  }

  static custom(validator: (value: any) => boolean, message: string): ValidationRule {
    return {
      type: ValidationType.CUSTOM,
      customValidator: validator,
      message
    };
  }
}

export const validationService = ValidationService;
